import { useEffect, useState } from "react";
import { useParams, Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { 
  ArrowLeft, MapPin, Calendar, Star, Phone, Mail, Globe, 
  Building2, CheckCircle, ExternalLink, Code2, Lock 
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BADGE_URLS = {
  gold: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/3qf4e4f0_Black%20And%20White%20Simple%20Icon%20Logo.png",
  silver: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/80ndwcjh_Black%20And%20White%20Simple%20Icon%20Logo%20%281%29.png",
  bronze: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/o5vrg8qt_Black%20And%20White%20Simple%20Icon%20Logo%20%282%29.png"
};

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const HotelDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHotel = async () => {
      try {
        const response = await axios.get(`${API}/hotels/${id}`);
        setHotel(response.data);
      } catch (err) {
        setError("Hotel not found");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchHotel();
    else setLoading(false);
  }, [id, user]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/signin" state={{ from: location.pathname }} replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-spin w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !hotel) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA]">
        <h1 className="font-heading text-3xl font-bold text-[#0B132B] mb-4">Hotel Not Found</h1>
        <Link 
          to="/#directory" 
          className="text-[#D4AF37] hover:text-[#92710d] flex items-center gap-2"
          data-testid="back-to-directory-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Directory
        </Link>
      </div>
    );
  }

  const hasCoordinates = hotel.latitude && hotel.longitude;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="hotel-detail-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/#directory" 
            className="inline-flex items-center gap-2 text-[#4A5568] hover:text-[#0B132B] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Directory</span>
          </Link>
        </div>
      </header>

      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img 
          src={hotel.image_url} 
          alt={hotel.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        
        {/* Rating Badge */}
        <div className="absolute top-6 right-6">
          <img 
            src={BADGE_URLS[hotel.rating]} 
            alt={`${hotel.rating} rating`}
            className="w-20 h-20 drop-shadow-lg"
          />
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border mb-4 ${
              hotel.rating === "gold" 
                ? "bg-[#FDF9EC] border-[#E8D38E] text-[#92710d]" 
                : hotel.rating === "silver" 
                ? "bg-[#F1F5F9] border-[#CBD5E1] text-[#64748b]" 
                : "bg-[#FAF2EA] border-[#E3BA94] text-[#a15f1c]"
            }`}>
              <Star className="w-4 h-4" />
              {hotel.rating.charAt(0).toUpperCase() + hotel.rating.slice(1)} Quality Hotel
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-2" data-testid="hotel-name">
              {hotel.name}
            </h1>
            <div className="flex items-center gap-2 text-white/90">
              <MapPin className="w-5 h-5" />
              <span className="text-lg">{hotel.location}, {hotel.country}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            <div className="bg-white p-8 border border-gray-100">
              <h2 className="font-heading text-2xl font-bold text-[#0B132B] mb-4">About This Hotel</h2>
              <p className="text-[#4A5568] leading-relaxed mb-4">
                {hotel.full_description || hotel.description}
              </p>
              <div className="flex items-center gap-2 text-[#718096] text-sm">
                {hotel.review_status === "pending_review" ? (
                  <span className="italic text-amber-700">Certification pending admin review</span>
                ) : (
                  <>
                    <Calendar className="w-4 h-4" />
                    <span>WTTQ Certified: {hotel.certified_date}</span>
                  </>
                )}
              </div>
            </div>

            {/* Amenities */}
            {hotel.amenities && hotel.amenities.length > 0 && (
              <div className="bg-white p-8 border border-gray-100">
                <h2 className="font-heading text-2xl font-bold text-[#0B132B] mb-6">Amenities & Services</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {hotel.amenities.map((amenity) => (
                    <div key={amenity} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                      <span className="text-[#4A5568]">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Map */}
            {hasCoordinates && (
              <div className="bg-white p-8 border border-gray-100">
                <h2 className="font-heading text-2xl font-bold text-[#0B132B] mb-6">Location</h2>
                <div className="h-[400px] rounded-lg overflow-hidden" data-testid="hotel-map">
                  <MapContainer 
                    center={[hotel.latitude, hotel.longitude]} 
                    zoom={13} 
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    <Marker position={[hotel.latitude, hotel.longitude]}>
                      <Popup>
                        <strong>{hotel.name}</strong><br />
                        {hotel.location}, {hotel.country}
                      </Popup>
                    </Marker>
                  </MapContainer>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info Card */}
            <div className="bg-white p-6 border border-gray-100">
              <h3 className="font-heading text-xl font-bold text-[#0B132B] mb-4">Hotel Information</h3>
              <div className="space-y-4">
                {hotel.rooms && (
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="text-sm text-[#718096]">Rooms</p>
                      <p className="text-[#0B132B] font-medium">{hotel.rooms} rooms</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-sm text-[#718096]">Location</p>
                    <p className="text-[#0B132B] font-medium">{hotel.location}, {hotel.country}</p>
                  </div>
                </div>
                {hotel.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="text-sm text-[#718096]">Phone</p>
                      <a href={`tel:${hotel.phone}`} className="text-[#0B132B] font-medium hover:text-[#D4AF37]">
                        {hotel.phone}
                      </a>
                    </div>
                  </div>
                )}
                {hotel.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="text-sm text-[#718096]">Email</p>
                      <a href={`mailto:${hotel.email}`} className="text-[#0B132B] font-medium hover:text-[#D4AF37] break-all">
                        {hotel.email}
                      </a>
                    </div>
                  </div>
                )}
                {hotel.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <p className="text-sm text-[#718096]">Website</p>
                      <a 
                        href={hotel.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#0B132B] font-medium hover:text-[#D4AF37] flex items-center gap-1"
                      >
                        Visit Website <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Certification Card */}
            <div className="bg-[#0B132B] p-6 text-white">
              <div className="flex items-center gap-4 mb-4">
                <img 
                  src={BADGE_URLS[hotel.rating]} 
                  alt={`${hotel.rating} rating`}
                  className="w-16 h-16"
                />
                <div>
                  <h3 className="font-heading text-xl font-bold">
                    {hotel.rating.charAt(0).toUpperCase() + hotel.rating.slice(1)} Certified
                  </h3>
                  <p className="text-white/70 text-sm">WTTQ Quality Label</p>
                </div>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">
                This hotel has been certified by WTTQ.Org for meeting our rigorous quality standards in hospitality excellence.
              </p>
              <Link
                to={user ? `/embed/hotel/${hotel.id}` : "/signin"}
                state={!user ? { from: `/embed/hotel/${hotel.id}` } : undefined}
                className="mt-5 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-[#D4AF37] text-[#0B132B] font-semibold text-sm hover:bg-[#E8C456] transition-colors"
                data-testid="get-embed-badge-button"
                title={user ? "Get embed badge" : "Partner-only — sign in to access"}
              >
                {user ? <><Code2 className="w-4 h-4" /> Get Embed Badge</> : <><Lock className="w-4 h-4" /> Sign in for Embed Badge</>}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0B132B] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-white/50 text-sm">
            &copy; {new Date().getFullYear()} WTTQ.Org. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HotelDetailPage;
